import { isMealPlanMain } from 'common/MealPlanUtils';
import { isoDateToUrlDate } from 'common/UrlDate';
import { MealPlanDisplayFoodItemFragment, MealPlanShefFragment } from 'src/gqlReactTypings.generated.d';
import type { CartForShef } from 'src/ztore/cart-store';

/**
 * Pure math for "how many units of a given food item can this user have for the
 * delivery date, given the shef's capacity and whatever they've already reserved
 * or claimed." Used by `useFoodItemAvailability` (the runtime availability hook).
 *
 * The server's `foodItem.availability[date].numAvailable` and `shef.availabilityForDate`
 * have already deducted this user's server-persisted reservation. Callers add that
 * reservation back via `servingsReservedFor*` to get the total capacity available
 * to this user, then subtract anything already in the active cart via `servingsClaimedFor*`.
 */
export interface FoodItemAvailabilityInputs {
  foodItem: MealPlanDisplayFoodItemFragment;
  shef: Pick<MealPlanShefFragment, 'availabilityForDate'>;
  deliveryDate: string | null;
  numServings: number;
  servingsReservedForFoodItem: number;
  servingsReservedForShef: number;
  servingsClaimedForFoodItem: number;
  servingsClaimedForShef: number;
}

export interface FoodItemAvailabilityOutputs {
  availableUnitsForFoodItem: number;
  availableUnitsForShef: number;
  numAvailableUnits: number;
  numAvailableServings: number;
}

export const getFoodItemAvailabilityUnits = ({
  foodItem,
  shef,
  deliveryDate,
  numServings,
  servingsReservedForFoodItem,
  servingsReservedForShef,
  servingsClaimedForFoodItem,
  servingsClaimedForShef,
}: FoodItemAvailabilityInputs): FoodItemAvailabilityOutputs => {
  const safeNumServings = numServings > 0 ? numServings : 1;
  const foodItemIsMain = isMealPlanMain(foodItem);

  const servingsAvailableForFoodItem = Math.max(
    0,
    foodItem.availability?.find((a) => isoDateToUrlDate(a.availabilityDate) === deliveryDate)?.numAvailable ?? 0
  );
  const remainingServingsForFoodItem =
    servingsAvailableForFoodItem + servingsReservedForFoodItem - servingsClaimedForFoodItem;
  const availableUnitsForFoodItem = foodItemIsMain
    ? Math.floor(remainingServingsForFoodItem / safeNumServings)
    : remainingServingsForFoodItem;

  const shefAvailabilityForDate = Math.max(0, shef.availabilityForDate ?? 0);
  const remainingServingsForShef = shefAvailabilityForDate + servingsReservedForShef - servingsClaimedForShef;
  const availableUnitsForShef = foodItemIsMain
    ? Math.floor(remainingServingsForShef / safeNumServings)
    : remainingServingsForShef;

  return {
    availableUnitsForFoodItem,
    availableUnitsForShef,
    numAvailableUnits: Math.min(availableUnitsForFoodItem, availableUnitsForShef),
    numAvailableServings: Math.min(remainingServingsForFoodItem, remainingServingsForShef),
  };
};

export interface FoodItemAvailabilityForCartsInputs {
  foodItem: MealPlanDisplayFoodItemFragment;
  shef: Pick<MealPlanShefFragment, 'availabilityForDate'>;
  deliveryDate: string | null;
  numServings: number;
  cartForShef: CartForShef | undefined;
  preEditCartForShef: CartForShef | undefined;
  isEditMode: boolean;
}

const sumServings = (cartForShef: CartForShef | undefined, foodItemId?: number) =>
  (cartForShef?.cartItems ?? [])
    .filter((item) => foodItemId === undefined || item.foodItem.id === foodItemId)
    .reduce((acc, item) => acc + item.quantity, 0);

/**
 * {@link getFoodItemAvailabilityUnits} for the user's live cart. Servings already in the
 * pre-edit cart count as reserved only while editing an order.
 */
export const getFoodItemAvailabilityForCarts = ({
  foodItem,
  shef,
  deliveryDate,
  numServings,
  cartForShef,
  preEditCartForShef,
  isEditMode,
}: FoodItemAvailabilityForCartsInputs): FoodItemAvailabilityOutputs =>
  getFoodItemAvailabilityUnits({
    foodItem,
    shef,
    deliveryDate,
    numServings,
    servingsReservedForFoodItem: isEditMode ? sumServings(preEditCartForShef, foodItem.id) : 0,
    servingsReservedForShef: isEditMode ? sumServings(preEditCartForShef) : 0,
    servingsClaimedForFoodItem: sumServings(cartForShef, foodItem.id),
    servingsClaimedForShef: sumServings(cartForShef),
  });
